import React from "react";
import { ArrowRight } from "lucide-react";

/**
 * Premium empty-state block with animated illustration, title, description, and 1–2 CTAs.
 *
 * Props:
 *   icon: Lucide icon component (rendered inside a decorative teal badge)
 *   title, description
 *   primary: { label, onClick, icon? }
 *   secondary: { label, onClick, icon? }
 *   testid
 */
const EmptyState = ({ icon: Icon, title, description, primary, secondary, testid, className = "" }) => (
    <div
        className={`relative border-2 border-dashed border-teal-300 bg-gradient-to-br from-teal-50/50 via-white to-slate-50 rounded-lg p-10 text-center overflow-hidden ${className}`}
        data-testid={testid || "empty-state"}
    >
        {/* Decorative radial dot pattern */}
        <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{ backgroundImage: "radial-gradient(#2EB4AC 1px, transparent 1px)", backgroundSize: "18px 18px" }}
        />
        <div className="relative">
            {Icon && (
                <div className="mx-auto h-16 w-16 rounded-2xl bg-white border border-teal-200 shadow-sm flex items-center justify-center relative">
                    <div className="absolute inset-0 rounded-2xl bg-teal-400/20 animate-ping opacity-40" aria-hidden="true" />
                    <Icon className="h-6 w-6 text-[#2EB4AC] relative" />
                </div>
            )}
            <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">{title}</h3>
            {description && (
                <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto leading-relaxed">{description}</p>
            )}
            {(primary || secondary) && (
                <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
                    {primary && (
                        <button
                            onClick={primary.onClick}
                            data-testid="empty-primary-cta"
                            className="inline-flex items-center gap-2 h-10 px-4 bg-[#2EB4AC] hover:bg-[#25908A] text-white rounded-lg font-medium text-sm transition-colors"
                        >
                            {primary.icon && <primary.icon className="h-4 w-4" />}
                            {primary.label}
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    )}
                    {secondary && (
                        <button
                            onClick={secondary.onClick}
                            data-testid="empty-secondary-cta"
                            className="inline-flex items-center gap-2 h-10 px-4 bg-white border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-slate-700 rounded-lg font-medium text-sm transition-colors"
                        >
                            {secondary.icon && <secondary.icon className="h-4 w-4" />}
                            {secondary.label}
                        </button>
                    )}
                </div>
            )}
        </div>
    </div>
);

export default EmptyState;
